import React, { useState, useEffect } from 'react';
import { X, User, Briefcase, DollarSign, FileText, Award, Shield, Calendar, Check, AlertCircle, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import PhoneInput from './PhoneInput';
import FieldTooltip from './FieldTooltip';
import ValidationIndicator from './ValidationIndicator';
import DragDropUploader from './shared/DragDropUploader';
import { useAutoSave } from '../hooks/useAutoSave';
import { COUNTRIES, getStatesForCountry, hasStates } from '../utils/countryStateData';
import { 
  CANDIDATE_STATUS_OPTIONS,
  EMPLOYMENT_PREFERENCE_OPTIONS,
  EMPLOYEE_TYPE_OPTIONS,
  YES_NO_NA_OPTIONS,
  PHONE_TYPE_OPTIONS,
  WORKDAY_SYNC_STATUS_OPTIONS,
  SCHEDULE_NEXT_ACTION_OPTIONS,
  PREFIX_OPTIONS,
  SUFFIX_OPTIONS,
  GENDER_OPTIONS
} from '../utils/candidateConstants';

const CandidateFormComprehensive = ({ candidate, currentUser, onClose, onSuccess }) => {
  const isEditMode = !!candidate;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  const [formData, setFormData] = useState({
    // ===== SECTION 1: PERSONAL INFORMATION =====
    prefix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    suffix: '',
    preferred_name: '',
    ssn: '',
    birth_date: '',
    gender: '',
    
    // ===== SECTION 2: CONTACT INFORMATION =====
    email: '',
    primary_phone: null,
    secondary_phone: null,
    alternate_email: '',
    linkedin_url: '',
    
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'United States',
    
    // ===== SECTION 3: CURRENT EMPLOYMENT =====
    current_title: '',
    current_company: '',
    current_salary: '',
    
    // ===== SECTION 4: EMPLOYMENT PREFERENCES =====
    employment_preference: '',
    desired_salary: '',
    desired_salary_currency: 'USD',
    desired_pay_rate: '',
    desired_pay_rate_currency: 'USD',
    date_available: '',
    preferred_location: '',
    desired_locations: [],
    willing_to_relocate: false,
    
    // ===== SECTION 5: PROFESSIONAL DETAILS (MANDATORY) =====
    years_of_experience: '',
    education_summary: '',
    reason_for_job_change: '',
    interview_availability: '',
    communication_notes: '',
    
    // ===== SECTION 6: BENEFITS & COMPLIANCE =====
    benefits: 'Not Applicable',
    employee_type: '',
    engagement_type: 'w2', // Required field: w2, contingent_1099, contingent_vendor
    rts_401k_plan: 'Not Applicable',
    health_benefits: 'Not Applicable',
    work_authorization_benefits: 'Not Applicable',
    immigrant_visa_sponsorship: 'Not Applicable',
    relocation_benefits: 'Not Applicable',
    type_of_phone: '',
    workday_sync_status: '',
    
    // ===== SECTION 7: WORK AUTHORIZATION =====
    work_authorization_status: 'authorized',
    visa_status: '',
    eligible_to_work_us: true,
    requires_sponsorship: false,
    
    // ===== SECTION 8: STATUS, OWNERSHIP & NEXT ACTION =====
    candidate_status: 'New Lead',
    candidate_owner: '',
    source: 'manual',
    source_detail: '',
    schedule_next_action: 'None',
    
    // Skills (AI-generated, editable)
    skills_summary: '',
    category: '',
    skills: [],
    
    // System
    location: '' // Primary location for legacy compatibility
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState({ checking: false, isDuplicate: false, matches: [] });
  const [showSourceDetail, setShowSourceDetail] = useState(false);
  const [showResumeUploader, setShowResumeUploader] = useState(false);
  
  // Auto-save
  const { loadDraft, clearDraft, lastSaved } = useAutoSave(
    `add-candidate-draft-${isEditMode ? candidate?.id : 'new'}`,
    formData,
    !isEditMode
  );

  // Duplicate detection - First + Last + Email + Address
  useEffect(() => {
    const checkDuplicate = async () => {
      if (!formData.first_name || !formData.last_name || !formData.email) {
        setDuplicateCheck({ checking: false, isDuplicate: false, matches: [] });
        return;
      }
      
      setDuplicateCheck({ checking: true, isDuplicate: false, matches: [] });
      
      try {
        const response = await fetch(`${backendUrl}/api/candidates`);
        if (response.ok) {
          const data = await response.json();
          const allCandidates = Array.isArray(data) ? data : [];
          
          const matches = allCandidates.filter(c => {
            if (c.id === candidate?.id) return false; // Skip self when editing
            
            const nameMatch = c.first_name?.toLowerCase() === formData.first_name.toLowerCase() &&
                             c.last_name?.toLowerCase() === formData.last_name.toLowerCase();
            const emailMatch = c.email?.toLowerCase() === formData.email.toLowerCase();
            const addressMatch = formData.address_line1 && c.address_line1 && 
                                c.address_line1.toLowerCase() === formData.address_line1.toLowerCase();
            
            return nameMatch || emailMatch || (nameMatch && addressMatch);
          });
          
          setDuplicateCheck({ 
            checking: false, 
            isDuplicate: matches.length > 0,
            matches: matches.slice(0, 3)
          });
        }
      } catch (error) {
        console.error('Error checking duplicate:', error);
        setDuplicateCheck({ checking: false, isDuplicate: false, matches: [] });
      }
    };
    
    const timer = setTimeout(checkDuplicate, 700);
    return () => clearTimeout(timer);
  }, [formData.first_name, formData.last_name, formData.email, formData.address_line1, backendUrl, candidate]);

  // Show/hide Source Detail field
  useEffect(() => {
    setShowSourceDetail(formData.source === 'Other' || formData.source === 'other');
  }, [formData.source]);

  // Load candidate data in edit mode
  useEffect(() => {
    if (isEditMode && candidate) {
      setFormData({
        // Personal Information
        prefix: candidate.prefix || '',
        first_name: candidate.first_name || '',
        middle_name: candidate.middle_name || '',
        last_name: candidate.last_name || '',
        suffix: candidate.suffix || '',
        preferred_name: candidate.preferred_name || '',
        ssn: candidate.ssn || '',
        birth_date: candidate.birth_date || '',
        gender: candidate.gender || '',
        
        // Contact Information
        email: candidate.email || '',
        primary_phone: candidate.primary_phone || null,
        secondary_phone: candidate.secondary_phone || null,
        alternate_email: candidate.alternate_email || '',
        linkedin_url: candidate.linkedin_url || '',
        
        // Address
        address_line1: candidate.address_line1 || '',
        address_line2: candidate.address_line2 || '',
        city: candidate.city || '',
        state: candidate.state || '',
        postal_code: candidate.postal_code || '',
        country: candidate.country || 'United States',
        
        // Current Employment
        current_title: candidate.current_title || '',
        current_company: candidate.current_company || '',
        current_salary: candidate.current_salary || '',
        
        // Employment Preferences
        employment_preference: candidate.employment_preference || '',
        desired_salary: candidate.desired_salary || '',
        desired_salary_currency: candidate.desired_salary_currency || 'USD',
        desired_pay_rate: candidate.desired_pay_rate || '',
        desired_pay_rate_currency: candidate.desired_pay_rate_currency || 'USD',
        date_available: candidate.date_available || '',
        preferred_location: candidate.preferred_location || '',
        desired_locations: candidate.desired_locations || [],
        willing_to_relocate: candidate.willing_to_relocate || false,
        
        // Professional Details
        years_of_experience: candidate.years_of_experience || '',
        education_summary: candidate.education_summary || '',
        reason_for_job_change: candidate.reason_for_job_change || '',
        interview_availability: candidate.interview_availability || '',
        communication_notes: candidate.communication_notes || '',
        
        // Benefits & Compliance
        benefits: candidate.benefits || 'Not Applicable',
        employee_type: candidate.employee_type || '',
        rts_401k_plan: candidate.rts_401k_plan || 'Not Applicable',
        health_benefits: candidate.health_benefits || 'Not Applicable',
        work_authorization_benefits: candidate.work_authorization_benefits || 'Not Applicable',
        immigrant_visa_sponsorship: candidate.immigrant_visa_sponsorship || 'Not Applicable',
        relocation_benefits: candidate.relocation_benefits || 'Not Applicable',
        type_of_phone: candidate.type_of_phone || '',
        workday_sync_status: candidate.workday_sync_status || '',
        
        // Work Authorization
        work_authorization_status: candidate.work_authorization_status || 'authorized',
        visa_status: candidate.visa_status || '',
        eligible_to_work_us: candidate.eligible_to_work_us !== undefined ? candidate.eligible_to_work_us : true,
        requires_sponsorship: candidate.requires_sponsorship || false,
        
        // Status, Ownership & Next Action
        candidate_status: candidate.candidate_status || candidate.status || 'New Lead',
        candidate_owner: candidate.candidate_owner || '',
        source: candidate.source || 'manual',
        source_detail: candidate.source_detail || '',
        schedule_next_action: candidate.schedule_next_action || 'None',
        
        // Skills
        skills: candidate.skills || [],
        
        // System
        location: candidate.location || ''
      });
    }
  }, [isEditMode, candidate]);

  // Load draft on mount
  useEffect(() => {
    if (!isEditMode) {
      const draft = loadDraft();
      if (draft && draft.first_name) {
        setShowDraftPrompt(true);
      }
    }
  }, [isEditMode, loadDraft]);

  const validate = () => {
    const newErrors = {};
    
    // Mandatory validations
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.birth_date) newErrors.birth_date = 'Birth date is required';
    
    // Birth date validation - cannot be in the future
    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (birthDate > today) {
        newErrors.birth_date = 'Birth date cannot be in the future';
      }
      
      // Optional: Check for reasonable age (e.g., at least 16 years old)
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 16);
      if (birthDate > minDate) {
        newErrors.birth_date = 'Candidate must be at least 16 years old';
      }
    }
    
    // Professional mandatory fields
    if (!formData.years_of_experience) {
      newErrors.years_of_experience = 'Years of experience is required';
    } else {
      const yearsExp = parseInt(formData.years_of_experience);
      if (isNaN(yearsExp)) {
        newErrors.years_of_experience = 'Years of experience must be a valid number';
      } else if (yearsExp < 0) {
        newErrors.years_of_experience = 'Years of experience cannot be negative';
      } else if (yearsExp > 50) {
        newErrors.years_of_experience = 'Years of experience cannot exceed 50 years';
      }
    }
    if (!formData.education_summary) newErrors.education_summary = 'Education is required';
    if (!formData.reason_for_job_change) newErrors.reason_for_job_change = 'Reason for job change is required';
    if (!formData.interview_availability) newErrors.interview_availability = 'Interview availability is required';
    if (!formData.communication_notes) newErrors.communication_notes = 'Communication notes are required';
    
    // Benefits mandatory fields
    if (!formData.employee_type) newErrors.employee_type = 'Employee type is required';
    
    // Desired pay rate validation - cannot be negative
    if (formData.desired_pay_rate) {
      const payRate = parseFloat(formData.desired_pay_rate);
      if (isNaN(payRate)) {
        newErrors.desired_pay_rate = 'Desired pay rate must be a valid number';
      } else if (payRate < 0) {
        newErrors.desired_pay_rate = 'Desired pay rate cannot be negative';
      }
    }
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    // Alternate email validation
    if (formData.alternate_email && !/\S+@\S+\.\S+/.test(formData.alternate_email)) {
      newErrors.alternate_email = 'Please enter a valid alternate email';
    }
    
    // Source detail validation
    if (showSourceDetail && !formData.source_detail) {
      newErrors.source_detail = 'Source detail is required when Source is "Other"';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResumeParsed = (parsedData) => {
    // Update form with parsed resume data
    setFormData(prev => ({
      ...prev,
      // Personal info
      first_name: parsedData.first_name || prev.first_name,
      last_name: parsedData.last_name || prev.last_name,
      email: parsedData.email || prev.email,
      phone: parsedData.phone || prev.phone,
      
      // Professional info
      category: parsedData.category || prev.category,
      skills_summary: parsedData.skills_summary || prev.skills_summary,
      skills: parsedData.skills || prev.skills,
      
      // Work experience
      current_employer: parsedData.current_employer || prev.current_employer,
      current_title: parsedData.current_title || prev.current_title,
      years_of_experience: parsedData.years_of_experience || prev.years_of_experience,
      
      // Education
      education: parsedData.education || prev.education,
      
      // Location
      city: parsedData.city || prev.city,
      state: parsedData.state || prev.state,
      country: parsedData.country || prev.country,
    }));
    
    toast.success('Resume parsed successfully! Review and adjust the auto-populated fields.');
    setShowResumeUploader(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (duplicateCheck.isDuplicate) {
      const proceed = window.confirm('Potential duplicate detected. Are you sure you want to proceed?');
      if (!proceed) return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        // Ensure required fields have values
        engagement_type: formData.engagement_type || 'w2', // Required field with default
        
        // Convert numeric fields to proper types
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
        
        // Convert current_salary to float if provided
        current_salary: formData.current_salary ? parseFloat(formData.current_salary) : null,
        
        // Convert salary/pay rate to expected format
        desired_salary: formData.desired_salary ? {
          amount: parseFloat(formData.desired_salary),
          currency: formData.desired_salary_currency
        } : null,
        desired_pay_rate: formData.desired_pay_rate ? {
          amount: parseFloat(formData.desired_pay_rate),
          currency: formData.desired_pay_rate_currency,
          period: 'hourly'
        } : null,
        
        // Remove empty strings and convert to null for optional fields
        primary_phone: formData.primary_phone || null,
        secondary_phone: formData.secondary_phone || null,
        
        // Set location for legacy compatibility (REQUIRED field)
        location: formData.preferred_location || formData.city || formData.state || 'Remote',
        // Auto-set owner if not specified
        candidate_owner: formData.candidate_owner || 'Current User',
        // Set primary_recruiter_id to current user (CRITICAL for visibility in candidate list!)
        primary_recruiter_id: currentUser?.id || null,
        primary_recruiter_name: currentUser?.full_name || currentUser?.name || null,
        // Set date_added (matches Juno schema - NOT user_date_added)
        date_added: new Date().toISOString()
      };
      
      const url = isEditMode 
        ? `${backendUrl}/api/candidates/${candidate.id}`
        : `${backendUrl}/api/candidates`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      console.log('[CandidateForm] Submitting data:', {
        url,
        method,
        dataKeys: Object.keys(submitData),
        numericFields: {
          years_of_experience: submitData.years_of_experience,
          current_salary: submitData.current_salary,
          desired_salary: submitData.desired_salary,
          desired_pay_rate: submitData.desired_pay_rate
        }
      });
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (response.ok) {
        clearDraft();
        toast.success(isEditMode ? 'Candidate updated successfully!' : 'Candidate created successfully!');
        onSuccess();
        onClose(); // Close modal after success
      } else {
        // Handle validation errors properly
        let errorMessage = 'Failed to save candidate';
        const fieldErrors = {};
        
        if (result.detail) {
          if (typeof result.detail === 'string') {
            errorMessage = result.detail;
          } else if (Array.isArray(result.detail)) {
            // Pydantic validation errors - map to field names
            console.log('Backend validation errors:', result.detail);
            
            const errorMessages = [];
            result.detail.forEach(err => {
              const fieldName = err.loc ? err.loc[err.loc.length - 1] : 'unknown';
              const message = err.msg || 'Invalid value';
              
              // Set field-specific error
              fieldErrors[fieldName] = message;
              
              // Build user-friendly message
              errorMessages.push(`${fieldName}: ${message}`);
            });
            
            errorMessage = errorMessages.join('\n');
            
            // Update errors state to highlight problematic fields
            setErrors(prev => ({ ...prev, ...fieldErrors }));
            
            // Scroll to first error field
            if (Object.keys(fieldErrors).length > 0) {
              const firstErrorField = Object.keys(fieldErrors)[0];
              setTimeout(() => {
                const element = document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  element.focus();
                }
              }, 100);
            }
          } else if (typeof result.detail === 'object') {
            errorMessage = JSON.stringify(result.detail);
          }
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving candidate:', error);
      toast.error('Error saving candidate: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-7xl my-8">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <User className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">{isEditMode ? 'Edit Candidate' : 'Add New Candidate'}</h2>
              <div className="flex items-center gap-3">
                <p className="text-blue-100 text-sm">Fields marked with * are required</p>
                {!isEditMode && lastSaved && (
                  <p className="text-blue-200 text-xs flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Auto-saved
                  </p>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Draft Restore */}
        {showDraftPrompt && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-yellow-800">You have an unsaved draft</span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const draft = loadDraft();
                  if (draft) {
                    setFormData(draft);
                    toast.success('Draft restored!');
                  }
                  setShowDraftPrompt(false);
                }}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
              >
                Restore
              </button>
              <button
                type="button"
                onClick={() => {
                  clearDraft();
                  setShowDraftPrompt(false);
                }}
                className="px-3 py-1 bg-white text-gray-700 text-sm rounded border hover:bg-gray-50"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {/* Duplicate Warning */}
        {duplicateCheck.isDuplicate && duplicateCheck.matches.length > 0 && (
          <div className="bg-orange-50 border-b border-orange-200 px-6 py-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-800">⚠️ Potential Duplicate Candidate(s) Found</p>
                <div className="mt-2 space-y-2">
                  {duplicateCheck.matches.map((match, idx) => (
                    <div key={idx} className="p-2 bg-white border border-orange-200 rounded text-xs">
                      <p className="font-medium text-gray-800">
                        {match.first_name} {match.last_name}
                      </p>
                      <p className="text-gray-600">{match.email} • {match.current_title || 'No title'}</p>
                      {match.location && <p className="text-gray-600">Location: {match.location}</p>}
                    </div>
                  ))}
                </div>
                <p className="text-orange-700 text-xs mt-2">
                  Please verify this isn't a duplicate. You can still proceed if this is a different person.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-150px)] overflow-y-auto">
          

          {/* RESUME UPLOAD SECTION */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Upload className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Upload Resume</h3>
                  <p className="text-xs text-gray-600">Auto-populate Category, Skills, and Skills Summary using AI</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowResumeUploader(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
                data-testid="upload-resume-btn"
              >
                <Upload className="h-4 w-4" />
                Upload Resume (.pdf, .docx)
              </button>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              💡 Upload resume to automatically fill Skills Summary, Category, and Skills fields
            </p>
          </div>

          {/* SECTION 1: PERSONAL INFORMATION */}
          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-purple-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Prefix */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                <select
                  value={formData.prefix}
                  onChange={(e) => setFormData({...formData, prefix: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select...</option>
                  {PREFIX_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      errors.first_name ? 'border-red-500' : 
                      formData.first_name.length >= 2 ? 'border-green-500' : 'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator isValid={formData.first_name.length >= 2} />
                </div>
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>
              
              {/* Middle Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Middle Name</label>
                <input
                  type="text"
                  value={formData.middle_name}
                  onChange={(e) => setFormData({...formData, middle_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      errors.last_name ? 'border-red-500' :
                      formData.last_name.length >= 2 ? 'border-green-500' : 'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator isValid={formData.last_name.length >= 2} />
                </div>
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>
              
              {/* Suffix */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Suffix</label>
                <select
                  value={formData.suffix}
                  onChange={(e) => setFormData({...formData, suffix: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select...</option>
                  {SUFFIX_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              
              {/* Preferred Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Name</label>
                <input
                  type="text"
                  value={formData.preferred_name}
                  onChange={(e) => setFormData({...formData, preferred_name: e.target.value})}
                  placeholder="Nickname"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Birth Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.birth_date}
                  onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.birth_date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.birth_date && <p className="text-red-500 text-xs mt-1">{errors.birth_date}</p>}
              </div>
              
              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select...</option>
                  {GENDER_OPTIONS.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              
              {/* SSN */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SSN
                  <FieldTooltip content="Social Security Number - kept confidential and encrypted" />
                </label>
                <input
                  type="password"
                  value={formData.ssn}
                  onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* SECTION 2: CONTACT INFORMATION */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Contact Information
            </h3>
            
            {/* Emails */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      errors.email || (duplicateCheck.isDuplicate && duplicateCheck.matches.some(m => m.email === formData.email)) ? 'border-red-500' :
                      formData.email && /\S+@\S+\.\S+/.test(formData.email) ? 'border-green-500' : 'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator 
                    isValid={formData.email && /\S+@\S+\.\S+/.test(formData.email) && !duplicateCheck.checking}
                  />
                </div>
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Email</label>
                <input
                  type="email"
                  value={formData.alternate_email}
                  onChange={(e) => setFormData({...formData, alternate_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            
            {/* Phone Numbers - Standardized */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <PhoneInput
                value={formData.primary_phone}
                onChange={(phone) => setFormData({...formData, primary_phone: phone})}
                label="Phone Number"
                required={true}
                placeholder="Primary phone"
              />
              
              <PhoneInput
                value={formData.secondary_phone}
                onChange={(phone) => setFormData({...formData, secondary_phone: phone})}
                label="Secondary Phone Number"
                placeholder="Alternate phone"
              />
            </div>
            
            {/* LinkedIn */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                LinkedIn <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                required
                value={formData.linkedin_url}
                onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                placeholder="https://linkedin.com/in/johndoe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            {/* Home Address - Traditional Layout */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Home Address <span className="text-red-500">*</span></p>
              
              <input
                type="text"
                required
                placeholder="Address Line 1"
                value={formData.address_line1}
                onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              
              <input
                type="text"
                placeholder="Address Line 2 (Optional)"
                value={formData.address_line2}
                onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                {hasStates(formData.country) ? (
                  <select
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select state...</option>
                    {getStatesForCountry(formData.country).map(state => (
                      <option key={state.code} value={state.code}>{state.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zip <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value, state: ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.name}>{country.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 3: CURRENT EMPLOYMENT */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-green-600" />
              Current Employment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Job Title</label>
                <input
                  type="text"
                  value={formData.current_title}
                  onChange={(e) => setFormData({...formData, current_title: e.target.value})}
                  placeholder="e.g., Senior Software Engineer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Company</label>
                <input
                  type="text"
                  value={formData.current_company}
                  onChange={(e) => setFormData({...formData, current_company: e.target.value})}
                  placeholder="e.g., Tech Corp"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Salary ($)</label>
                <input
                  type="number"
                  value={formData.current_salary}
                  onChange={(e) => setFormData({...formData, current_salary: e.target.value})}
                  placeholder="100000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* SECTION 4: EMPLOYMENT PREFERENCES */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Employment Preferences
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Employment Preference */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Preference</label>
                <select
                  value={formData.employment_preference}
                  onChange={(e) => setFormData({...formData, employment_preference: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select...</option>
                  {EMPLOYMENT_PREFERENCE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              {/* Desired Salary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Desired Salary</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.desired_salary}
                    onChange={(e) => setFormData({...formData, desired_salary: e.target.value})}
                    placeholder="120000"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <select
                    value={formData.desired_salary_currency}
                    onChange={(e) => setFormData({...formData, desired_salary_currency: e.target.value})}
                    className="w-24 px-2 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              
              {/* Desired Pay Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Desired Pay Rate ($/hour)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.desired_pay_rate}
                    onChange={(e) => setFormData({...formData, desired_pay_rate: e.target.value})}
                    placeholder="75"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <select
                    value={formData.desired_pay_rate_currency}
                    onChange={(e) => setFormData({...formData, desired_pay_rate_currency: e.target.value})}
                    className="w-24 px-2 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              
              {/* Date Available */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Available</label>
                <input
                  type="date"
                  value={formData.date_available}
                  onChange={(e) => setFormData({...formData, date_available: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">When can candidate start?</p>
              </div>
              
              {/* Preferred Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Location</label>
                <input
                  type="text"
                  value={formData.preferred_location}
                  onChange={(e) => setFormData({...formData, preferred_location: e.target.value})}
                  placeholder="e.g., San Francisco, CA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Willing to Relocate */}
              <div className="flex items-center h-full pt-8">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.willing_to_relocate}
                    onChange={(e) => setFormData({...formData, willing_to_relocate: e.target.checked})}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Willing to Relocate</span>
                </label>
              </div>
            </div>

              {/* Desired Locations - Multi-select (max 10) */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Desired Locations (Max 10)
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {formData.desired_locations.map((loc, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                        {loc}
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            desired_locations: formData.desired_locations.filter((_, i) => i !== idx)
                          })}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {formData.desired_locations.length < 10 && (
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value && !formData.desired_locations.includes(e.target.value)) {
                          setFormData({
                            ...formData,
                            desired_locations: [...formData.desired_locations, e.target.value]
                          });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Add location...</option>
                      <option value="Onsite">Onsite</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                      {getStatesForCountry('United States').map(state => (
                        <option key={state.code} value={state.name}>{state.name}</option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-gray-500">
                    Select up to 10 locations. Can include states or work arrangements (Onsite/Remote/Hybrid)
                  </p>
                </div>
              </div>

          </div>

          {/* SECTION 5: PROFESSIONAL DETAILS (All MANDATORY) */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-blue-600" />
              Professional Details <span className="text-sm text-gray-600">(Required for compliance)</span>
            </h3>
            <div className="space-y-4">
              {/* Years of Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Years of Experience <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  required
                  value={formData.years_of_experience}
                  onChange={(e) => setFormData({...formData, years_of_experience: e.target.value})}
                  min="0"
                  max="50"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.years_of_experience ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.years_of_experience && <p className="text-red-500 text-xs mt-1">{errors.years_of_experience}</p>}
              </div>
              
              {/* Education */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.education_summary}
                  onChange={(e) => setFormData({...formData, education_summary: e.target.value})}
                  rows={3}
                  placeholder="e.g., Bachelor's in Computer Science from MIT, Master's in Business Administration"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.education_summary ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.education_summary && <p className="text-red-500 text-xs mt-1">{errors.education_summary}</p>}
              </div>
              
              {/* Reason for Job Change */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Job Change <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.reason_for_job_change}
                  onChange={(e) => setFormData({...formData, reason_for_job_change: e.target.value})}
                  rows={2}
                  placeholder="e.g., Seeking growth opportunities, career advancement, better work-life balance"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.reason_for_job_change ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.reason_for_job_change && <p className="text-red-500 text-xs mt-1">{errors.reason_for_job_change}</p>}
              </div>
              
              {/* Interview Availability */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interview Availability <span className="text-red-500">*</span>
                  <FieldTooltip content="Notice period, specific days, time constraints (e.g., 'Available next week' or 'Wednesdays only')" />
                </label>
                <textarea
                  required
                  value={formData.interview_availability}
                  onChange={(e) => setFormData({...formData, interview_availability: e.target.value})}
                  rows={2}
                  placeholder="e.g., Available next week, 2 weeks notice, Mondays and Wednesdays after 4pm"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.interview_availability ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.interview_availability && <p className="text-red-500 text-xs mt-1">{errors.interview_availability}</p>}
              </div>
              
              {/* Communication Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Communication Assessment <span className="text-red-500">*</span>
                  <FieldTooltip content="Recruiter notes on communication quality, accent, English proficiency, responsiveness" />
                </label>
                <textarea
                  required
                  value={formData.communication_notes}
                  onChange={(e) => setFormData({...formData, communication_notes: e.target.value})}
                  rows={2}
                  placeholder="e.g., Excellent English skills, responsive via email, professional phone manner"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.communication_notes ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.communication_notes && <p className="text-red-500 text-xs mt-1">{errors.communication_notes}</p>}
              </div>

              {/* Skills Summary - AI-generated but editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills Summary
                  <FieldTooltip content="AI-generated from resume. You can edit this field if needed." />
                </label>
                <textarea
                  value={formData.skills_summary}
                  onChange={(e) => setFormData({...formData, skills_summary: e.target.value})}
                  rows={4}
                  placeholder="Will be auto-populated from resume upload..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
                />
                <p className="text-xs text-blue-600 mt-1">💡 Upload resume to auto-generate</p>
              </div>
              
              {/* Category - AI-populated */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                  <FieldTooltip content="Auto-populated from resume using AI (e.g., Software Developer, DevOps Engineer)" />
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="Will be auto-populated from resume..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
                />
              </div>

            </div>
          </div>

          {/* SECTION 6: BENEFITS & COMPLIANCE */}
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              Benefits & Compliance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Benefits */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Benefits <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.benefits}
                  onChange={(e) => setFormData({...formData, benefits: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {YES_NO_NA_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              {/* Employee Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.employee_type}
                  onChange={(e) => setFormData({...formData, employee_type: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
                    errors.employee_type ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select...</option>
                  {EMPLOYEE_TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {errors.employee_type && <p className="text-red-500 text-xs mt-1">{errors.employee_type}</p>}
              </div>
              
              {/* RTS 401K Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  RTS 401K Plan <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.rts_401k_plan}
                  onChange={(e) => setFormData({...formData, rts_401k_plan: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {YES_NO_NA_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              {/* Health Benefits */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Health Benefits <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.health_benefits}
                  onChange={(e) => setFormData({...formData, health_benefits: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {YES_NO_NA_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              {/* Optional Benefits */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Work Authorization Benefits</label>
                <select
                  value={formData.work_authorization_benefits}
                  onChange={(e) => setFormData({...formData, work_authorization_benefits: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {YES_NO_NA_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Immigrant Visa Sponsorship</label>
                <select
                  value={formData.immigrant_visa_sponsorship}
                  onChange={(e) => setFormData({...formData, immigrant_visa_sponsorship: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {YES_NO_NA_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Relocation Benefits</label>
                <select
                  value={formData.relocation_benefits}
                  onChange={(e) => setFormData({...formData, relocation_benefits: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {YES_NO_NA_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type of Phone</label>
                <select
                  value={formData.type_of_phone}
                  onChange={(e) => setFormData({...formData, type_of_phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select...</option>
                  {PHONE_TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Workday Sync Status</label>
                <select
                  value={formData.workday_sync_status}
                  onChange={(e) => setFormData({...formData, workday_sync_status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select...</option>
                  {WORKDAY_SYNC_STATUS_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 7: WORK AUTHORIZATION */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Work Authorization <span className="text-red-500">*</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Authorization Status <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.work_authorization_status}
                  onChange={(e) => setFormData({...formData, work_authorization_status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="authorized">Authorized to work</option>
                  <option value="needs_sponsorship">Needs sponsorship</option>
                  <option value="green_card">Green card holder</option>
                  <option value="citizen">US Citizen</option>
                  <option value="student_visa">Student visa</option>
                </select>
              </div>
              
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.eligible_to_work_us}
                    onChange={(e) => setFormData({...formData, eligible_to_work_us: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Eligible to work in US</span>
                </label>
                
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_sponsorship}
                    onChange={(e) => setFormData({...formData, requires_sponsorship: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Requires Sponsorship</span>
                </label>
              </div>
            </div>
          </div>

          {/* SECTION 8: STATUS, OWNERSHIP & SOURCE */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              Status, Ownership & Next Action
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Candidate Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Candidate Status <span className="text-red-500">*</span>
                  <FieldTooltip content="New Lead: Just added, never contacted • Active: Spoken to, ready for jobs • Unavailable: Hired elsewhere • Hired: Hired by us • DNU/DNW: Do not use (red flags)" />
                </label>
                <select
                  required
                  value={formData.candidate_status}
                  onChange={(e) => setFormData({...formData, candidate_status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  {CANDIDATE_STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              {/* Candidate Owner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate Owner</label>
                <input
                  type="text"
                  value={formData.candidate_owner}
                  onChange={(e) => setFormData({...formData, candidate_owner: e.target.value})}
                  placeholder="Leave blank to assign to yourself"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">Primary recruiter for this candidate</p>
              </div>
              
              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="indeed">Indeed</option>
                  <option value="monster">Monster</option>
                  <option value="referral">Referral</option>
                  <option value="career_site">Career Site</option>
                  <option value="agency">Agency</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Source Detail - Conditional */}
              {showSourceDetail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source Detail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.source_detail}
                    onChange={(e) => setFormData({...formData, source_detail: e.target.value})}
                    placeholder="Specify the source"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      errors.source_detail ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.source_detail && <p className="text-red-500 text-xs mt-1">{errors.source_detail}</p>}
                </div>
              )}
              
              {/* Schedule Next Action */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Next Action</label>
                <div className="space-y-2">
                  {SCHEDULE_NEXT_ACTION_OPTIONS.map(action => (
                    <label key={action} className="flex items-center">
                      <input
                        type="radio"
                        name="schedule_next_action"
                        value={action}
                        checked={formData.schedule_next_action === action}
                        onChange={(e) => setFormData({...formData, schedule_next_action: e.target.value})}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">{action}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t-2 border-gray-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium shadow-lg"
            >
              {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Candidate' : 'Create Candidate')}
            </button>
          </div>
        </form>
      </div>

      {/* Resume Upload Modal */}
      {showResumeUploader && (
        <DragDropUploader
          uploadType="resume"
          onClose={() => setShowResumeUploader(false)}
          onFileParsed={handleResumeParsed}
        />
      )}
    </div>
  );
};

export default CandidateFormComprehensive;
